'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '../../utils/cn';

const stackVariants = cva('flex', {
  variants: {
    direction: { row: 'flex-row', column: 'flex-col' },
    gap: {
      none: 'gap-0',
      xs: 'gap-1',
      sm: 'gap-2',
      md: 'gap-4',
      lg: 'gap-6',
      xl: 'gap-8',
    },
    align: {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
      baseline: 'items-baseline',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
      around: 'justify-around',
    },
    wrap: { wrap: 'flex-wrap', nowrap: 'flex-nowrap' },
  },
  defaultVariants: { direction: 'column', gap: 'md', align: 'stretch', wrap: 'nowrap' },
});

export interface UzStackProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof stackVariants> {}

/**
 * Flex layout helper. Use instead of writing `flex flex-col gap-4` everywhere.
 * Encourages consistent spacing scale across the app.
 */
export const UzStack = forwardRef<HTMLDivElement, UzStackProps>(
  ({ direction, gap, align, justify, wrap, className, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(stackVariants({ direction, gap, align, justify, wrap }), className)}
      {...rest}
    />
  ),
);
UzStack.displayName = 'UzStack';
